import { api } from "./api";
import type { AuditAction } from "@/constants/audit";

// Re-exported so existing importers keep working; the vocabulary lives in @/constants/audit.
export type { AuditAction };

export interface AuditLogEntry {
  id: string;
  project_id: string;
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
  action: AuditAction | string;
  stage: string | null;
  before: string | null;
  after: string | null;
  comment: string | null;
  user: string | null;
  user_name: string | null;
  created_at: string;
}

export interface AuditLogFilters {
  stage?: string;
  action?: string;
  user?: string;
  entityType?: string;
  entityId?: string;
  limit?: number;
  offset?: number;
}

export interface RecordAuditEventInput {
  projectId: string;
  entityType: string;
  entityId: string;
  entityName?: string | null;
  action: AuditAction;
  stage?: string | null;
  before?: string | null;
  after?: string | null;
  comment?: string | null;
}

function buildQuery(projectId: string, filters?: AuditLogFilters): string {
  const params = new URLSearchParams({ project_id: projectId });
  if (filters?.stage) params.set("stage", filters.stage);
  if (filters?.action) params.set("action", filters.action);
  if (filters?.user) params.set("user", filters.user);
  if (filters?.entityType) params.set("entity_type", filters.entityType);
  if (filters?.entityId) params.set("entity_id", filters.entityId);
  if (filters?.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters?.offset !== undefined) params.set("offset", String(filters.offset));
  return params.toString();
}

export const auditLogApi = {
  getAuditLog: (projectId: string, filters?: AuditLogFilters): Promise<AuditLogEntry[]> =>
    api.get(`/api/audit/entries?${buildQuery(projectId, filters)}`),

  recordAuditEvent: async (input: RecordAuditEventInput): Promise<void> => {
    try {
      await api.post("/api/audit/entries", {
        project_id: input.projectId,
        entity_type: input.entityType,
        entity_id: input.entityId,
        entity_name: input.entityName ?? null,
        action: input.action,
        stage: input.stage ?? null,
        before: input.before ?? null,
        after: input.after ?? null,
        comment: input.comment ?? null,
      });
    } catch (err) {
      // Logging an audit entry must never block or fail the action it describes.
      console.error("Failed to record audit event:", err);
    }
  },
};
